'use strict';

var appResources = window.appResources;
var urls = appResources.urls;
var pendingPids = new Set();
var fetchTimeout = null;

/**
 * Module for handling dynamic quick add button loading on product tiles.
 * Required a separate call to add the button to the tile becasue tiles are cached.
 */

/**
 * Checks if current page is a product listing or search page
 * @returns {boolean} True if on listing/search page
 */
function isListingOrSearchPage() {
    return $('.product-grid').length > 0 || $('.custom-search-container').length > 0;
}

/**
 * Collects PIDs from product tiles that need quick add buttons
 * Filters out PIDs that have already been processed
 * @param {jQuery|Element[]} tiles - jQuery collection or array of tile elements
 * @returns {Array<string>} Array of product IDs that haven't been processed yet
 */
function collectPidsFromTiles(tiles) {
    var pids = [];
    $(tiles).each(function () {
        var $tile = $(this);
        var $productContainer = $tile.closest('.product');
        var pid = $productContainer.data('pid');

        // Only collect PIDs for tiles that are not yet processed
        if (pid && !$tile.attr('data-quick-add-processed')) {
            pids.push(pid);
        }
    });
    return pids;
}

/**
 * Fetches quick add button HTML from server for given PIDs
 * @param {Array<string>} pids - Array of product IDs (should already be filtered for unprocessed PIDs)
 */
function fetchQuickAddButtons(pids) {
    var url = urls.dynamicTileDataUrl;
    if (!pids || pids.length === 0 || !url) {
        return;
    }

    $.ajax({
        url: url,
        type: 'GET',
        dataType: 'json',
        data: {
            pids: pids.join(',')
        }
    }).done(function (response) {
        if (response && response.buttons) {
            Object.keys(response.buttons).forEach(function (pid) {
                var buttonHtml = response.buttons[pid];
                var $productContainer = $('.product[data-pid="' + pid + '"]');
                var $tile = $productContainer.find('.product-tile');
                $tile.attr('data-quick-add-processed', 'true');

                if ($tile.length) {
                    // Find or create container for quick add button
                    var $quickAddContainer = $productContainer.find('.image-container .quick-add-product-container');
                    if ($quickAddContainer.length > 0) {
                        // Insert quick add button HTML
                        $quickAddContainer.html(buttonHtml);
                    }
                }
            });
        }
    }).fail(function (xhr) {
        console.error('Failed to fetch quick add buttons:', xhr); // eslint-disable-line no-console
    });
}

/**
 * Batches PIDs and fetches quick add buttons with debouncing
 * Filters out PIDs that have already been processed using data-quick-add-processed
 * @param {Array<string>} pids - Array of product IDs to fetch
 */
function batchFetchQuickAddButtons(pids) {
    if (!pids || pids.length === 0) {
        return;
    }

    // Add PIDs to pending set for debouncing
    // Note: We'll filter by data-quick-add-processed right before fetching
    pids.forEach(function (pid) {
        pendingPids.add(pid);
    });

    // Clear existing timeout
    if (fetchTimeout) {
        clearTimeout(fetchTimeout);
    }

    // Debounce: wait 100ms to batch multiple requests
    fetchTimeout = setTimeout(function () {
        if (pendingPids.size > 0) {
            // Filter out PIDs that have already been processed by checking DOM attribute
            // This ensures we don't fetch for tiles that were processed between adding to pendingPids and now
            var pidsArray = Array.from(pendingPids).filter(function (pid) {
                var $productContainer = $('.product[data-pid="' + pid + '"]');
                var $tile = $productContainer.find('.product-tile');
                return $tile.length > 0 && !$tile.attr('data-quick-add-processed');
            });

            pendingPids.clear();

            if (pidsArray.length > 0) {
                fetchQuickAddButtons(pidsArray);
            }
        }
    }, 100);
}

/**
 * Processes visible product tiles and fetches quick add buttons
 * @param {boolean} isCarousel - Whether this is a carousel page
 */
function processProductTiles(isCarousel) {
    var tiles = document.querySelectorAll('.product .product-tile:not([data-quick-add-processed])');

    if (tiles.length === 0) {
        return;
    }

    var pids = collectPidsFromTiles(tiles);

    if (pids.length > 0) {
        if (isCarousel) {
            // For carousel, fetch immediately and disconnect observer after
            fetchQuickAddButtons(pids);
        } else {
            // For listing/search, batch fetch with debouncing
            batchFetchQuickAddButtons(pids);
        }
    }
}

/**
 * IntersectionObserver callback for handling quick add button decoration
 * Only used for carousel pages or dynamic content changes
 * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries
 * @param {IntersectionObserver} observer - The intersection observer instance
 */
function productTileObserverCallback(entries, observer) {
    var isCarousel = !isListingOrSearchPage();
    var tilesToProcess = [];

    entries.forEach(function (entry) {
        if (entry.isIntersecting) {
            var $tile = $(entry.target);
            if (!$tile.attr('data-quick-add-processed')) {
                tilesToProcess.push(entry.target);
            }

            if (isCarousel) {
                // For carousel, unobserve after processing
                observer.unobserve(entry.target);
            }
        }
    });

    if (tilesToProcess.length > 0) {
        var pids = collectPidsFromTiles(tilesToProcess);
        if (pids.length > 0) {
            if (isCarousel) {
                fetchQuickAddButtons(pids);
                // Disconnect observer after first batch for carousel
                observer.disconnect();
            } else {
                // For listing/search pages, batch fetch with debouncing
                // This handles dynamic content changes (show more, filters)
                batchFetchQuickAddButtons(pids);
            }
        }
    }
}

/**
 * Module for handling dynamic quick add button loading
 */
module.exports = {
    /**
     * Initialize quick add button decoration with observer pattern
     * Fetches quick add button HTML via ajax and inserts into product tiles
     */
    init: function () {
        $('body').ready(function () {
            var observers = {
                intersection: null,
                mutation: null
            };

            var isCarousel = !isListingOrSearchPage();

            // Check for IntersectionObserver support
            var hasIntersectionObserver = 'IntersectionObserver' in window;

            if (!hasIntersectionObserver) {
                console.warn('IntersectionObserver not supported, using fallback method'); // eslint-disable-line no-console
                return;
            }

            /**
             * Processes all tiles currently in the DOM and makes a single batch call
             * Used for listing/search pages on initial load
             */
            function processAllTilesOnPage() {
                var tiles = document.querySelectorAll('.product .product-tile:not([data-quick-add-processed])');
                if (tiles.length > 0) {
                    var pids = collectPidsFromTiles(tiles);
                    if (pids.length > 0) {
                        // Single batch call for all tiles on page load
                        fetchQuickAddButtons(pids);
                    }
                }
            }

            /**
             * Handle new content being added to the page via MutationObserver
             * Collects all new PIDs and makes a batch call
             * @param {MutationRecord[]} mutations - Array of mutation records
             */
            function handleContentChanges(mutations) {
                if (isCarousel) {
                    return; // Carousel handled separately
                }

                var hasProductGridChange = false;
                var hasNewTiles = false;

                mutations.forEach(function (mutation) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        // Check if any added nodes contain product tiles or grid content
                        mutation.addedNodes.forEach(function (node) {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                var $node = $(node);

                                if ($node.is('.product .product-tile')
                                    || $node.find('.product .product-tile').length > 0) {
                                    hasNewTiles = true;
                                }

                                // Check for carousel containers (carousels can exist on listing/search pages)
                                if ($node.is('.carousel-inner') || $node.find('.carousel-inner').length > 0
                                    || $node.is('.einstein-carousel') || $node.find('.einstein-carousel').length > 0
                                    || $node.is('.experience-slides') || $node.find('.experience-slides').length > 0) {
                                    hasNewTiles = true;
                                }

                                // Track if entire product grid is being replaced (filter/sort scenario)
                                if ($node.is('.product-grid')
                                    || $node.find('.product-grid').length > 0) {
                                    hasProductGridChange = true;
                                    hasNewTiles = true;

                                    // Reset processed flags when grid changes
                                    $node.find('.product-tile').removeAttr('data-quick-add-processed');
                                }
                            }
                        });
                    }
                });

                if (hasNewTiles) {
                    // Wait for DOM to update, then collect ALL new PIDs and make a single batch call
                    var delay = hasProductGridChange ? 200 : 100;
                    setTimeout(function () {
                        var tiles = document.querySelectorAll('.product .product-tile:not([data-quick-add-processed])');
                        if (tiles.length > 0) {
                            var pids = collectPidsFromTiles(tiles);
                            if (pids.length > 0) {
                                // Single batch call for all new tiles
                                fetchQuickAddButtons(pids);
                            }
                        }
                    }, delay);
                }
            }

            // Create intersection observer if supported
            if (hasIntersectionObserver) {
                observers.intersection = new IntersectionObserver(productTileObserverCallback, {
                    root: null,
                    rootMargin: '50px',
                    threshold: 0.1
                });
            }

            // Set up mutation observer for dynamic content
            if ('MutationObserver' in window) {
                observers.mutation = new MutationObserver(handleContentChanges);
                observers.mutation.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            }

            // Initial observation of existing product tiles
            if (isCarousel) {
                // For carousel, wait a bit for tiles to load, then process once
                setTimeout(function () {
                    processProductTiles(true);
                    if (observers.intersection) {
                        observers.intersection.disconnect();
                    }
                }, 300);
            } else {
                // For listing/search pages: Wait for DOM to fully load,
                // then collect ALL PIDs from DOM and make a single batch call
                // Wait for DOM to be fully rendered (we're already in body.ready, but wait for tiles)
                // Use window.load to ensure all resources are loaded, or setTimeout as fallback
                if (document.readyState === 'complete') {
                    // Page already fully loaded
                    setTimeout(processAllTilesOnPage, 150);
                    return;
                }
                // Wait for window load event to ensure all tiles are rendered
                $(window).on('load', function () {
                    setTimeout(processAllTilesOnPage, 100);
                });
                // Fallback: if load event already fired, process after short delay
                setTimeout(function () {
                    if (document.readyState === 'complete') {
                        processAllTilesOnPage();
                    }
                }, 500);
            }

            // Listen for carousel setup events (from einsteinCarousel or similar)
            // Process carousel tiles on both content/pdp pages and listing/search pages
            $('body').on('carousel:setup', function () {
                setTimeout(function () {
                    // Process carousel tiles specifically - they may be in .carousel-inner, .einstein-carousel, .experience-slides, or .scroll-area
                    var carouselTiles = document.querySelectorAll('.carousel-inner .product .product-tile:not([data-quick-add-processed]), .einstein-carousel .product .product-tile:not([data-quick-add-processed]), .experience-slides .product .product-tile:not([data-quick-add-processed]), .scroll-area .product .product-tile:not([data-quick-add-processed])');
                    if (carouselTiles.length > 0) {
                        var carouselPids = collectPidsFromTiles(carouselTiles);
                        if (carouselPids.length > 0) {
                            // For pure carousel pages, fetch immediately; for listing/search pages, use batching
                            if (!isListingOrSearchPage()) {
                                fetchQuickAddButtons(carouselPids);
                                if (observers.intersection) {
                                    observers.intersection.disconnect();
                                }
                            } else {
                                // On listing/search pages, batch fetch with debouncing
                                batchFetchQuickAddButtons(carouselPids);
                            }
                        }
                    }
                }, 200);
            });

            // Cleanup observers when page unloads
            $(window).on('beforeunload', function () {
                if (observers.intersection) {
                    observers.intersection.disconnect();
                }
                if (observers.mutation) {
                    observers.mutation.disconnect();
                }
                if (fetchTimeout) {
                    clearTimeout(fetchTimeout);
                }
            });
        });
    }
};
